import { useState } from 'react';
import { Box, Modal, Stack, Text } from '@mantine/core';
import { Button } from 'src/design-system/Button';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AttendeeRow } from './AttendeeRow';

type Props = {
  opened: boolean;
  onClose: () => void;
  familyId: string;
  onConfirm?: (selectedClientIds: string[]) => void;
};

export function SelectAgendaMembersModal({ opened, onClose, familyId, onConfirm }: Props) {
  const { family } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const { familyRoles } = useFamilyRoles();
  const [unchecked, setUnchecked] = useState<Record<string, boolean>>({});

  const members = buildMembers({ family, clients, familyRoles });

  // Baseline: everyone is included unless explicitly toggled off.
  const isChecked = (id: string) => !unchecked[id];

  const toggle = (id: string) => setUnchecked(previous => ({ ...previous, [id]: !previous[id] }));

  const handleConfirm = () => {
    onConfirm?.(members.filter(member => isChecked(member.id)).map(member => member.id));
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Select members to include in the meeting agenda"
      size={440}
    >
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" mb="sm">
        Confirm attendees
      </Text>

      <Stack gap="xxxs">
        {members.map(member => (
          <AttendeeRow
            key={member.id}
            name={member.name}
            role={member.role}
            checked={isChecked(member.id)}
            onToggle={() => toggle(member.id)}
            ariaLabel={`${member.name} included`}
          />
        ))}
      </Stack>

      <Box mt="lg">
        <Button fullWidth onClick={handleConfirm}>
          Confirm
        </Button>
      </Box>
    </Modal>
  );
}

type Family = NonNullable<ReturnType<typeof useFamily>['family']>;
type Client = ReturnType<typeof useClients>['clients'][number];
type FamilyRole = ReturnType<typeof useFamilyRoles>['familyRoles'][number];

function buildMembers({
  family,
  clients,
  familyRoles,
}: {
  family: Family | null;
  clients: Client[];
  familyRoles: FamilyRole[];
}): { id: string; name: string; role: string }[] {
  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));
  const roleNameById = Object.fromEntries(familyRoles.map(role => [role.id, role.name]));

  const roleByClientId: Record<string, string> = {};
  if (family?.mainFamilyMember) {
    const roleId = family.mainFamilyMember.familyRoleId;
    roleByClientId[family.mainFamilyMember.clientId] =
      (roleId && roleNameById[roleId]) || 'Family member';
  }
  for (const member of family?.members ?? []) {
    roleByClientId[member.clientId] =
      (member.familyRoleId && roleNameById[member.familyRoleId]) ||
      roleByClientId[member.clientId] ||
      'Family member';
  }
  for (const secondary of family?.secondaryFamilyMembers ?? []) {
    roleByClientId[secondary.clientId] ||=
      secondary.relationshipToMainFamilyMember ?? 'Family member';
  }

  const seen = new Set<string>();
  const memberIds = [
    family?.mainFamilyMember.clientId,
    ...(family?.secondaryFamilyMembers ?? []).map(s => s.clientId),
    ...(family?.members ?? []).map(m => m.clientId),
  ].filter((id): id is string => !!id && !seen.has(id) && (seen.add(id), true));

  return memberIds
    .map(clientId => {
      const client = clientById[clientId];
      if (!client) return null;
      return {
        id: clientId,
        name: `${client.firstName} ${client.lastName}`.trim(),
        role: roleByClientId[clientId] ?? 'Family member',
      };
    })
    .filter((member): member is { id: string; name: string; role: string } => member !== null);
}
